import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackTableCopyRequested } from '@/analytics/utils/table-analytics'
import { TableCopyMethod } from '@/analytics/enums/table.enums'
import SelectedCellsPill from './SelectedCellsPill'
import TableCellEditMenu from './TableCellEditMenu'
import { useCellSelection, type CellRange } from '../hooks/useCellSelection'
import { copyCellMatrix, writeCellClipboard } from '../utils/copy-cells'
import type { TableEditResult } from '../schemas'
import { Tooltip } from '@/components/ui/tooltip/Tooltip'
import { remainingCapacity, additionLimitMessage } from '../capacity'
import { TableMatchContext } from './table-match-context'
import type { TableMatch } from '../hooks/useTableSearch'
import { copyTableSelection } from '../utils/copy-content'
import AddColumnControl from './AddColumnControl'
import Checkbox from '@/components/checkbox/Checkbox'
import { tableStyles } from './table-styles'
import EditColumnDialog from './EditColumnDialog'
import { AnalysisRun } from '../analysis/schemas'
import TableFileCell from './TableFileCell'
import TableColumnHeader from './TableColumnHeader'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AlertErrorCircle, Plus, TrashDelete, XClose } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { TablePage, TableQuery, TableEdit, TableRow, TableColumn, ColumnKind } from '../schemas'
import TableDataCell from './TableDataCell'
import TableCellOverflow from './TableCellOverflow'
import TableRowResizeHandle from './TableRowResizeHandle'
import type { TableCellDrafts } from '../hooks/useTableCellDrafts'
import { Button } from '@/components/button/Button'
import { defaultTableLayout, moveColumn, orderedColumns, TableLayout } from '../layout'

/** Virtualizes the current server page; the backend owns all ordering and filtering. */
export default function TableGrid({
  cellDrafts,
  data,
  analysisItems,
  analysisCostLimitReached = false,
  query,
  canEdit,
  saving,
  onEdit,
  onAddRow,
  addedRowRequest = 0,
  addRowLabel = 'Add row',
  onSort,
  onDeleteRows,
  onDeleteColumn,
  onColumnHidden,
  layout = defaultTableLayout,
  layoutDisabled = false,
  onLayoutChange,
  fillAvailableHeight = false,
  pagination,
  activeMatch = null,
}: {
  pagination?: ReactNode
  activeMatch?: TableMatch | null
  fillAvailableHeight?: boolean
  cellDrafts: TableCellDrafts
  layout?: TableLayout
  layoutDisabled?: boolean
  onLayoutChange?: (update: (layout: TableLayout) => TableLayout) => void
  analysisItems?: AnalysisRun['sources']
  analysisCostLimitReached?: boolean
  data: TablePage
  query: TableQuery
  canEdit: boolean
  saving: boolean
  addRowLabel?: string
  onAddRow: () => void
  addedRowRequest?: number
  onEdit: (edit: TableEdit) => Promise<TableEditResult>
  onSort: (columnId: string, descending?: boolean) => void
  onDeleteRows: (rowIds: string[]) => void
  onDeleteColumn: (columnId: string) => void
  onColumnHidden?: () => void
}) {
  const selectionScope = JSON.stringify([data.table.id, query])
  const [selection, setSelection] = useState<{ scope: string; ids: string[] }>({ scope: selectionScope, ids: [] })
  useEffect(() => {
    setSelection({ scope: selectionScope, ids: [] })
  }, [selectionScope])
  const selectedIds = selection.scope === selectionScope ? selection.ids.filter((id) => data.rows.some((row) => row.id === id)) : []
  const extraColumnCount = canEdit ? 2 : 0
  const [editingColumn, setEditingColumn] = useState<TableColumn | null>(null)
  const columnHeaders = useRef(new Map<string, HTMLTableCellElement>())
  const dragColumn = useRef<string | null>(null)
  const columnIds = useMemo(() => [...(data.document_based ? ['row'] : []), ...data.columns.map((column) => column.id)], [data.document_based, data.columns])
  const order = useMemo(
    () => orderedColumns(columnIds, data.document_based && !layout.order.includes('row') ? { ...layout, order: ['row', ...layout.order] } : layout),
    [columnIds, data.document_based, layout]
  )
  const columnsById = useMemo(() => new Map(data.columns.map((column) => [column.id, column])), [data.columns])
  const cellsByRow = useMemo(() => new Map(data.rows.map((row) => [row.id, new Map(row.cells.map((cell) => [cell.column_id, cell]))])), [data.rows])
  const viewport = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (viewport.current) viewport.current.scrollTop = 0
  }, [selectionScope])
  const handledAddedRow = useRef(0)
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null)
  useEffect(() => {
    if (!addedRowRequest || handledAddedRow.current === addedRowRequest || saving || !data.rows.length) return
    handledAddedRow.current = addedRowRequest
    setHighlightedRow(data.rows[0].id)
    if (viewport.current) {
      viewport.current.scrollTop = 0
      viewport.current.scrollIntoView({ block: 'nearest', behavior: 'instant' })
    }
  }, [addedRowRequest, saving, data.rows])
  useEffect(() => {
    if (!highlightedRow) return
    const timeout = window.setTimeout(() => setHighlightedRow(null), 2000)
    return () => window.clearTimeout(timeout)
  }, [highlightedRow, addedRowRequest])
  const analysisByRow = useMemo(() => new Map(analysisItems?.map((item) => [item.row_id, item])), [analysisItems])
  const columns = useMemo<ColumnDef<TableRow>[]>(
    () => [
      ...(data.document_based
        ? [
            {
              id: 'row',
              header: () => (
                <TableColumnHeader
                  id="row"
                  name="File"
                  kind={ColumnKind.Text}
                  isFile
                  saving={saving}
                  layoutDisabled={layoutDisabled}
                  layout={layout}
                  order={order}
                  columnIds={columnIds}
                  onLayoutChange={onLayoutChange}
                  onColumnHidden={onColumnHidden}
                />
              ),
              size: 240,
              minSize: 100,
              cell: ({ row }) => {
                const analysis = analysisByRow.get(row.id)
                return (
                  <div className="flex items-start justify-between gap-2 px-4 py-2 text-sm text-brand-neutral-600">
                    <div data-table-copy-content className="min-w-0">
                      {analysis && (
                        <div className="text-xs">
                          <TableFileCell
                            matterId={data.table.matter_id}
                            document={{
                              id: analysis.document_id ?? null,
                              name: row.original.source_filename ?? row.original.search_filename ?? analysis.document_name ?? 'Document',
                            }}
                          />
                          {analysis.status === 'failed' ? (
                            <span className="mt-1 flex items-start gap-1 text-red-800">
                              <AlertErrorCircle className="h-4 w-4 shrink-0" aria-hidden />
                              <span>{analysis.error ?? 'Document failed'}</span>
                            </span>
                          ) : analysis.status !== 'completed' ? (
                            <span className="block">
                              {analysisCostLimitReached && (analysis.status === 'ready' || analysis.status === 'cancelled')
                                ? 'Analysis paused'
                                : analysis.status === 'ready'
                                ? 'Awaiting analysis'
                                : ['pending', 'running'].includes(analysis.status)
                                ? 'Running'
                                : analysis.status === 'cancelled'
                                ? 'Cancelled'
                                : analysis.status}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                )
              },
            } satisfies ColumnDef<TableRow>,
          ]
        : []),
      ...data.columns.map(
        (column): ColumnDef<TableRow> => ({
          id: column.id,
          size: 280,
          minSize: 160,
          header: () => {
            return (
              <TableColumnHeader
                id={column.id}
                name={column.name}
                kind={column.kind}
                sort={query.sorts.find((item) => item.column_id === column.id)}
                onSort={(descending) => (descending === undefined ? onSort(column.id) : onSort(column.id, descending))}
                onEdit={canEdit ? () => setEditingColumn(column) : undefined}
                onDelete={canEdit ? () => onDeleteColumn(column.id) : undefined}
                manualEntry={data.document_based && column.manual_entry}
                pendingAnalysis={data.document_based && column.pending_analysis}
                analysisRunning={column.analysis_running}
                saving={saving}
                layoutDisabled={layoutDisabled}
                layout={layout}
                order={order}
                columnIds={columnIds}
                onLayoutChange={onLayoutChange}
                onColumnHidden={onColumnHidden}
              />
            )
          },
        })
      ),
    ],
    [
      analysisByRow,
      analysisCostLimitReached,
      data.columns,
      data.document_based,
      data.table.matter_id,
      query,
      canEdit,
      saving,
      onSort,
      onDeleteColumn,
      onColumnHidden,
      onLayoutChange,
      layout,
      layoutDisabled,
      order,
      columnIds,
    ]
  )
  const table = useReactTable({
    data: data.rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    state: {
      columnOrder: order,
      columnVisibility: Object.fromEntries(layout.hidden.map((id) => [id, false])),
      columnPinning: { left: order.filter((id) => layout.pinned.includes(id)), right: [] },
      columnSizing: { ...layout.widths, ...(layout.entry_width ? { row: layout.entry_width } : {}) },
    },
    columnResizeMode: 'onEnd',
    onColumnSizingChange: (update) =>
      onLayoutChange?.((current) => {
        const previous = { ...current.widths, ...(current.entry_width ? { row: current.entry_width } : {}) }
        const next = typeof update === 'function' ? update(previous) : update
        const { row, ...widths } = next
        return { ...current, widths, entry_width: row ?? current.entry_width }
      }),
  })
  const visibleIds = [...table.getLeftVisibleLeafColumns(), ...table.getCenterVisibleLeafColumns(), ...table.getRightVisibleLeafColumns()].map(
    (column) => column.id
  )
  const filenames = new Map(analysisItems?.map((item) => [item.row_id, item.document_name ?? 'Document']))
  const selectedValues = (range: CellRange, footnotes: boolean) =>
    copyCellMatrix(
      data.rows.slice(Math.min(range.anchor.row, range.end.row), Math.max(range.anchor.row, range.end.row) + 1),
      visibleIds.slice(Math.min(range.anchor.column, range.end.column), Math.max(range.anchor.column, range.end.column) + 1),
      data.table.matter_id,
      data.table.id,
      footnotes,
      filenames
    )
  const cellSelection = useCellSelection(
    JSON.stringify([selectionScope, visibleIds, data.rows.map((row) => row.id)]),
    data.rows.length,
    visibleIds.length,
    selectedValues
  )
  const selectedCellCount = cellSelection.range
    ? (Math.abs(cellSelection.range.end.row - cellSelection.range.anchor.row) + 1) *
      (Math.abs(cellSelection.range.end.column - cellSelection.range.anchor.column) + 1)
    : 0
  const [copyError, setCopyError] = useState('')
  useEffect(() => setCopyError(''), [cellSelection.range])
  const copyPending = useRef(false)
  const { trackEvent } = useAnalytics()
  const copySelected = async (footnotes: boolean, method = TableCopyMethod.Action, range = cellSelection.range) => {
    if (!range) return
    if (copyPending.current) throw new Error('A copy is already in progress')
    copyPending.current = true
    try {
      trackTableCopyRequested(trackEvent, {
        tableId: data.table.id,
        tableCopyMethod: method,
        withFootnotes: footnotes,
        selectedCellCount: (Math.abs(range.end.row - range.anchor.row) + 1) * (Math.abs(range.end.column - range.anchor.column) + 1),
      })
      await writeCellClipboard(selectedValues(range, footnotes))
      setCopyError('')
    } finally {
      copyPending.current = false
    }
  }
  const latestCopy = useRef(copySelected)
  useLayoutEffect(() => {
    latestCopy.current = copySelected
  })
  const copyCellAt = useCallback(
    (row: number, column: number, footnotes: boolean, method = TableCopyMethod.Action) =>
      latestCopy.current(footnotes, method, { anchor: { row, column }, end: { row, column } }),
    []
  )
  const rows = table.getRowModel().rows
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => viewport.current,
    estimateSize: () => 100,
    overscan: 10,
    getItemKey: (index) => rows[index].id,
  })
  const sizingKey = JSON.stringify([layout.widths, layout.entry_width, layout.row_heights, layout.hidden, layout.order, layout.pinned])
  useEffect(() => {
    virtualizer.measure()
  }, [virtualizer, sizingKey])
  const items = virtualizer.getVirtualItems()
  const scrolledMatch = useRef<TableMatch | null>(null)
  const activeRowIndex = activeMatch ? rows.findIndex((row) => row.id === activeMatch.rowId) : -1
  useEffect(() => {
    if (activeRowIndex >= 0 && !virtualizer.getVirtualItems().some((item) => item.index === activeRowIndex)) {
      virtualizer.scrollToIndex(activeRowIndex, { align: 'auto' })
    }
  }, [activeRowIndex, activeMatch, virtualizer])
  useEffect(() => {
    if (!activeMatch) {
      scrolledMatch.current = null
      return
    }
    if (scrolledMatch.current === activeMatch) return
    const mark = viewport.current?.querySelector<HTMLElement>('[data-active-search-match]')
    const cell = mark?.closest('td')
    if (!mark || !cell) return
    // Reveal the occurrence inside an overflowing cell, then the cell below the sticky header.
    mark.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    cell.style.scrollMarginTop = `${viewport.current?.querySelector('thead')?.getBoundingClientRect().height ?? 0}px`
    cell.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    scrolledMatch.current = activeMatch
  }, [activeMatch, items])
  const scrolledSelection = useRef<CellRange | null>(null)
  useEffect(() => {
    const end = cellSelection.range?.end
    if (!end || scrolledSelection.current === cellSelection.range) return
    scrolledSelection.current = cellSelection.range
    if (!virtualizer.getVirtualItems().some((item) => item.index === end.row)) virtualizer.scrollToIndex(end.row, { align: 'auto' })
    cellSelection.tableRef.current
      ?.querySelector(`[data-copy-row="${end.row}"][data-copy-column="${end.column}"]`)
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [cellSelection.range, virtualizer, cellSelection.tableRef])
  const top = items[0]?.start ?? 0
  const bottom = items.length ? virtualizer.getTotalSize() - items[items.length - 1].end : 0
  return (
    <>
      {table.getState().columnSizingInfo.isResizingColumn && <div aria-hidden className="fixed inset-0 z-[1001] cursor-col-resize select-none" />}
      {editingColumn && (
        <EditColumnDialog
          key={editingColumn.id}
          column={editingColumn}
          anchor={columnHeaders.current.get(editingColumn.id)}
          documentBased={data.document_based}
          saving={saving}
          onSave={onEdit}
          onClose={() => setEditingColumn(null)}
        />
      )}
      <p role="status" aria-label="Cell selection" aria-atomic="true" className="sr-only">
        {cellSelection.range
          ? `${selectedCellCount} ${selectedCellCount === 1 ? 'cell' : 'cells'} selected. Row ${query.offset + cellSelection.range.end.row + 1}, column ${
              visibleIds[cellSelection.range.end.column] === 'row'
                ? data.document_based
                  ? 'File'
                  : 'Entry'
                : columnsById.get(visibleIds[cellSelection.range.end.column])?.name ?? ''
            }.`
          : 'No cells selected'}
      </p>
      {copyError && (
        <p role="alert" className="text-xs text-brand-neutral-800">
          {copyError}
        </p>
      )}
      {selectedCellCount > 1 && (
        <SelectedCellsPill
          allowFootnotes={Boolean(data.document_based)}
          key={JSON.stringify(cellSelection.range)}
          tableRef={cellSelection.tableRef}
          count={selectedCellCount}
          onCopy={copySelected}
          onClear={cellSelection.clear}
        />
      )}
      {canEdit && selectedIds.length > 0 && (
        <div role="toolbar" aria-label="Selected rows" className="flex shrink-0 items-center gap-2 text-sm">
          <IconButton
            icon={<XClose />}
            size="sm"
            variant="plain"
            color="neutral"
            aria-label="Clear row selection"
            disabled={saving}
            onClick={() => setSelection({ scope: selectionScope, ids: [] })}
          />
          <span role="status">
            {selectedIds.length} {selectedIds.length === 1 ? 'item' : 'items'} selected
          </span>
          <Button size="sm" variant="plain" color="destructive" leadingIcon={<TrashDelete />} disabled={saving} onClick={() => onDeleteRows(selectedIds)}>
            Delete
          </Button>
        </div>
      )}
      <div ref={viewport} className={`flex flex-col ${tableStyles.container} ${fillAvailableHeight ? tableStyles.filledContainer : ''}`}>
        <table
          ref={cellSelection.tableRef}
          tabIndex={0}
          aria-description="Click or drag to select cells. Shift-click or Shift and arrow keys extend selection. Double-click or Enter selects text or edits a cell. Copy defaults to no footnotes. Escape clears selection."
          onPointerDownCapture={cellSelection.pointerDown}
          onDragStart={(event) => {
            if (event.target instanceof Element && event.target.closest('[data-copy-row]')) event.preventDefault()
          }}
          onDoubleClickCapture={(event) => {
            if (event.target instanceof Element && !event.target.closest('a, button')) cellSelection.doubleClick(event.target)
          }}
          onClickCapture={(event) => {
            if (cellSelection.suppressClick()) {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
          onKeyDownCapture={(event) => {
            if (
              event.target === event.currentTarget &&
              cellSelection.range &&
              !cellSelection.textCell &&
              (!event.shiftKey || data.document_based) &&
              (event.metaKey || event.ctrlKey) &&
              !event.altKey &&
              event.key.toLowerCase() === 'c'
            ) {
              event.preventDefault()
              if (copyPending.current) return
              setCopyError('')
              void copySelected(event.shiftKey, TableCopyMethod.Shortcut).catch(() => setCopyError('Unable to copy. Please try again.'))
              return
            }
            cellSelection.keyDown(event)
          }}
          onCopyCapture={(event) => {
            if (
              event.target instanceof Element &&
              event.target.closest('input, textarea, [contenteditable]:not([contenteditable="false"]):not([role="textbox"])')
            )
              return
            if (cellSelection.range && !cellSelection.textCell && !(event.target instanceof Element && event.target.closest('[role="textbox"]'))) {
              if (copyPending.current) {
                event.preventDefault()
                event.stopPropagation()
                return
              }
              trackTableCopyRequested(trackEvent, {
                tableId: data.table.id,
                tableCopyMethod: TableCopyMethod.Clipboard,
                selectedCellCount,
                withFootnotes: false,
              })
            } else if (window.getSelection()?.isCollapsed === false) {
              trackTableCopyRequested(trackEvent, { tableId: data.table.id, tableCopyMethod: TableCopyMethod.Clipboard })
            }
          }}
          onCopy={(event) => {
            if (event.target instanceof Element && event.target.closest('input, textarea, [contenteditable]:not([contenteditable="false"])')) return
            if (cellSelection.copy(event)) setCopyError('')
            else copyTableSelection(event)
          }}
          aria-label={data.table.name}
          className={`${tableStyles.table} shrink-0`}
          style={{ width: table.getTotalSize() + (canEdit ? 232 : 0) }}
        >
          <thead className={tableStyles.head}>
            <tr>
              {canEdit && (
                <th scope="col" className={tableStyles.headerCell} style={{ width: 32 }}>
                  <span className="sr-only">Row</span>
                </th>
              )}
              {table.getFlatHeaders().map((header) => (
                <th
                  key={header.id}
                  ref={(element) => {
                    if (element) columnHeaders.current.set(header.id, element)
                    else columnHeaders.current.delete(header.id)
                  }}
                  tabIndex={-1}
                  scope="col"
                  aria-sort={
                    query.sorts.find((sort) => sort.column_id === header.column.id)?.descending === true
                      ? 'descending'
                      : query.sorts.some((sort) => sort.column_id === header.column.id)
                      ? 'ascending'
                      : 'none'
                  }
                  draggable={Boolean(onLayoutChange) && !layoutDisabled && !table.getState().columnSizingInfo.isResizingColumn}
                  className={`group/header ${tableStyles.headerCell} ${onLayoutChange && !layoutDisabled ? 'cursor-grab active:cursor-grabbing' : ''} ${
                    header.column.getIsPinned() && header.column.getIsLastColumn('left') ? 'border-r-2 border-r-brand-neutral-500' : ''
                  }`}
                  style={{
                    width: header.getSize(),
                    position: header.column.getIsPinned() ? 'sticky' : 'relative',
                    left: header.column.getIsPinned() ? header.column.getStart('left') : undefined,
                    zIndex: header.column.getIsResizing() ? 3 : header.column.getIsPinned() ? 2 : undefined,
                  }}
                  onDragStart={(event) => {
                    if (!onLayoutChange || layoutDisabled || header.column.getIsResizing()) {
                      event.preventDefault()
                      return
                    }
                    dragColumn.current = header.id
                    event.dataTransfer.setData('text/plain', header.id)
                    event.dataTransfer.effectAllowed = 'move'
                  }}
                  onDragEnd={() => {
                    dragColumn.current = null
                  }}
                  onDragOver={(event) => {
                    if (
                      !layoutDisabled &&
                      dragColumn.current &&
                      columnIds.includes(header.id) &&
                      layout.pinned.includes(dragColumn.current) === layout.pinned.includes(header.id)
                    )
                      event.preventDefault()
                  }}
                  onDrop={(event) => {
                    if (
                      layoutDisabled ||
                      !dragColumn.current ||
                      !columnIds.includes(header.id) ||
                      layout.pinned.includes(dragColumn.current) !== layout.pinned.includes(header.id)
                    )
                      return
                    event.preventDefault()
                    const from = dragColumn.current
                    dragColumn.current = null
                    onLayoutChange?.((current) => moveColumn(columnIds, current, from, header.id))
                  }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {onLayoutChange && header.column.getCanResize() && (
                    <div
                      role="separator"
                      aria-label={`Resize ${header.id === 'row' ? 'File' : data.columns.find((column) => column.id === header.id)?.name}`}
                      aria-orientation="vertical"
                      aria-valuenow={Math.round(header.getSize())}
                      aria-valuemin={header.column.columnDef.minSize}
                      tabIndex={layoutDisabled ? -1 : 0}
                      className={`absolute right-0 top-0 h-full w-1 cursor-col-resize touch-none hover:bg-brand-neutral-400 focus:bg-brand-neutral-400 ${
                        header.column.getIsResizing() ? 'bg-brand-neutral-400' : ''
                      }`}
                      style={{ transform: header.column.getIsResizing() ? `translateX(${table.getState().columnSizingInfo.deltaOffset ?? 0}px)` : undefined }}
                      onMouseDown={(event) => {
                        if (layoutDisabled || event.button !== 0) return
                        event.preventDefault()
                        event.stopPropagation()
                        header.getResizeHandler()(event)
                      }}
                      onTouchStart={layoutDisabled ? undefined : header.getResizeHandler()}
                      onKeyDown={(event) => {
                        if (layoutDisabled || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return
                        event.preventDefault()
                        const width = Math.max(header.column.columnDef.minSize ?? 20, header.getSize() + (event.key === 'ArrowRight' ? 10 : -10))
                        onLayoutChange((current) =>
                          header.id === 'row' ? { ...current, entry_width: width } : { ...current, widths: { ...current.widths, [header.id]: width } }
                        )
                      }}
                    />
                  )}
                </th>
              ))}
              {canEdit && (
                <th scope="col" className={`${tableStyles.headerCell} px-2 py-2`} style={{ width: 200 }}>
                  <AddColumnControl
                    limitMessage={additionLimitMessage(remainingCapacity(data.capacity, 'columns'), 1, 'columns')}
                    inTable
                    documentBased={Boolean(data.document_based)}
                    saving={saving}
                    loading={false}
                    onSave={onEdit}
                  />
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {top > 0 && (
              <tr aria-hidden="true">
                <td colSpan={table.getVisibleLeafColumns().length + extraColumnCount} style={{ height: top }} />
              </tr>
            )}
            {items.map((item) => {
              const row = rows[item.index]
              const rowEditing = Object.values(cellDrafts.drafts[row.id] ?? {}).some(Boolean)
              const rowHeight = layout.row_heights[row.id]
              const resizeHandle = onLayoutChange && (
                <TableRowResizeHandle
                  rowNumber={query.offset + row.index + 1}
                  height={rowHeight}
                  measuredHeight={item.size}
                  disabled={layoutDisabled}
                  onResize={(height) => onLayoutChange((current) => ({ ...current, row_heights: { ...current.row_heights, [row.id]: height } }))}
                />
              )
              return (
                <tr
                  key={row.id}
                  data-index={item.index}
                  ref={virtualizer.measureElement}
                  className="align-top border-b border-brand-neutral-200"
                  onClick={() => row.id === highlightedRow && setHighlightedRow(null)}
                  onKeyDown={() => row.id === highlightedRow && setHighlightedRow(null)}
                >
                  {canEdit && (
                    <td className={`relative group/selection ${tableStyles.selectionCell}`}>
                      <label className="relative mx-auto flex h-8 w-8 cursor-pointer items-center justify-center">
                        <span
                          aria-hidden
                          className={
                            selectedIds.includes(row.id)
                              ? 'invisible'
                              : 'text-xs tabular-nums text-brand-neutral-600 group-hover/selection:invisible group-focus-within/selection:invisible'
                          }
                        >
                          {query.offset + row.index + 1}
                        </span>
                        <span
                          className={
                            'absolute inset-0 flex items-center justify-center ' +
                            (selectedIds.includes(row.id) ? '' : 'opacity-0 group-hover/selection:opacity-100 group-focus-within/selection:opacity-100')
                          }
                        >
                          <Checkbox
                            checked={selectedIds.includes(row.id)}
                            disabled={saving}
                            onCheckedChange={(checked) =>
                              setSelection({ scope: selectionScope, ids: checked ? [...selectedIds, row.id] : selectedIds.filter((id) => id !== row.id) })
                            }
                          />
                        </span>
                        <span className="sr-only">Select row {query.offset + row.index + 1}</span>
                      </label>
                      {resizeHandle}
                    </td>
                  )}
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const value = cellsByRow.get(row.id)?.get(cell.column.id)
                    const column = columnsById.get(cell.column.id)
                    const draft = cellDrafts.drafts[row.id]?.[cell.column.id] ?? null
                    const selecting = cellSelection.isSelected(row.index, cellIndex) && !draft && !cellSelection.isTextCell(row.index, cellIndex)
                    const copyCell = (footnotes: boolean, method?: TableCopyMethod) =>
                      copySelected(footnotes, method, { anchor: { row: row.index, column: cellIndex }, end: { row: row.index, column: cellIndex } })
                    return (
                      <td
                        key={cell.id}
                        data-copy-row={row.index}
                        data-copy-column={cellIndex}
                        tabIndex={-1}
                        data-copy-selected={cellSelection.isSelected(row.index, cellIndex)}
                        aria-description={cellSelection.isSelected(row.index, cellIndex) ? 'Selected for copying' : undefined}
                        className={`${cellSelection.isTextCell(row.index, cellIndex) || draft ? 'select-text' : 'select-none'} p-0 ${tableStyles.cellBorder} ${
                          tableStyles.cellFocus
                        } ${row.id === highlightedRow ? 'bg-brand-100 motion-safe:animate-row-highlight' : tableStyles.cellBackground} ${
                          cell.column.getIsPinned() && cell.column.getIsLastColumn('left') ? 'border-r-2 border-r-brand-neutral-500' : ''
                        }`}
                        style={{
                          position: cell.column.getIsPinned() ? 'sticky' : 'relative',
                          left: cell.column.getIsPinned() ? cell.column.getStart('left') : undefined,
                          zIndex: cell.column.getIsPinned() ? 1 : 0,
                        }}
                      >
                        {selecting && (
                          <span
                            aria-hidden
                            data-cell-selection
                            className="pointer-events-none absolute inset-0 z-[2] border-brand-500 bg-brand-100/30"
                            style={{
                              borderTopWidth: cellSelection.isSelected(row.index - 1, cellIndex) ? 0 : 1,
                              borderBottomWidth: cellSelection.isSelected(row.index + 1, cellIndex) ? 0 : 1,
                              borderLeftWidth: cellSelection.isSelected(row.index, cellIndex - 1) ? 0 : 1,
                              borderRightWidth: cellSelection.isSelected(row.index, cellIndex + 1) ? 0 : 1,
                            }}
                          />
                        )}
                        {cellSelection.isTextCell(row.index, cellIndex) && !draft && (
                          <span aria-hidden className="pointer-events-none absolute inset-0 z-[2] border border-brand-500" />
                        )}
                        {column ? (
                          <TableDataCell
                            tableId={data.table.id}
                            rowId={row.id}
                            rowNumber={query.offset + row.index + 1}
                            column={column}
                            cell={value}
                            draft={draft}
                            changeDraft={cellDrafts.change}
                            onEdit={onEdit}
                            matterId={data.table.matter_id}
                            canEdit={canEdit}
                            onCopy={copyCellAt}
                            copyRow={row.index}
                            copyColumn={cellIndex}
                            selected={selectedCellCount === 1 && cellSelection.isSelected(row.index, cellIndex)}
                            hideActions={
                              selectedCellCount > 1 ||
                              (selectedCellCount === 1 && !cellSelection.isSelected(row.index, cellIndex)) ||
                              cellSelection.isTextCell(row.index, cellIndex)
                            }
                            saving={saving}
                            pending={Boolean(
                              analysisByRow.has(row.id) && analysisByRow.get(row.id)?.status !== 'failed' && !column.manual_entry && column.question && !value
                            )}
                            documentBased={Boolean(data.document_based)}
                            rowEditing={rowEditing || cellSelection.isTextCell(row.index, cellIndex)}
                            height={rowHeight}
                            search={query.search?.trim() ?? ''}
                            activeOffset={activeMatch?.rowId === row.id && activeMatch.columnId === column.id ? activeMatch.offset : undefined}
                          />
                        ) : (
                          <TableMatchContext.Provider
                            value={
                              query.search?.trim()
                                ? {
                                    offsets: row.original.filename_search_offsets ?? [],
                                    length: Array.from(query.search.trim()).length,
                                    activeOffset: activeMatch?.rowId === row.id && activeMatch.columnId === 'row' ? activeMatch.offset : undefined,
                                  }
                                : null
                            }
                          >
                            <TableCellOverflow
                              editing={
                                rowEditing ||
                                cellSelection.isTextCell(row.index, cellIndex) ||
                                (activeMatch?.rowId === row.id && activeMatch.columnId === 'row')
                              }
                              height={rowHeight}
                            >
                              <TableCellEditMenu
                                enabled={false}
                                onEdit={() => {}}
                                onCopy={copyCell}
                                selected={selectedCellCount === 1 && cellSelection.isSelected(row.index, cellIndex)}
                                hideActions={
                                  selectedCellCount > 1 ||
                                  (selectedCellCount === 1 && !cellSelection.isSelected(row.index, cellIndex)) ||
                                  cellSelection.isTextCell(row.index, cellIndex)
                                }
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCellEditMenu>
                            </TableCellOverflow>
                          </TableMatchContext.Provider>
                        )}
                        {!canEdit && cellIndex === 0 && resizeHandle}
                      </td>
                    )
                  })}
                  {canEdit && <td className={`${tableStyles.cellBorder} ${tableStyles.cellBackground}`} />}
                </tr>
              )
            })}
            {bottom > 0 && (
              <tr aria-hidden="true">
                <td colSpan={table.getVisibleLeafColumns().length + extraColumnCount} style={{ height: bottom }} />
              </tr>
            )}
            {canEdit && !data.document_based && data.columns.length > 0 && (
              <tr>
                <td colSpan={table.getVisibleLeafColumns().length + extraColumnCount} className="px-3 py-2 bg-brand-neutral-50/50">
                  <Tooltip title={additionLimitMessage(remainingCapacity(data.capacity, 'rows'), 1, data.document_based ? 'documents' : 'rows') ?? ''}>
                    <span tabIndex={remainingCapacity(data.capacity, 'rows') === 0 ? 0 : undefined}>
                      <Button
                        size="sm"
                        variant="plain"
                        leadingIcon={<Plus />}
                        disabled={saving || remainingCapacity(data.capacity, 'rows') === 0}
                        onClick={onAddRow}
                      >
                        {addRowLabel}
                      </Button>
                    </span>
                  </Tooltip>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {!data.rows.length && (
          <p className="shrink-0 p-6 text-brand-neutral-600">
            {query.search?.trim()
              ? 'No entries match this search.'
              : query.filters.length
              ? 'No entries match these filters.'
              : !canEdit
              ? 'No entries yet.'
              : data.document_based
              ? 'No entries yet. Add documents to get started.'
              : canEdit && data.columns.length === 0
              ? 'No entries yet. Add a column to get started.'
              : 'No entries yet. Add a row to get started.'}
          </p>
        )}
        {pagination}
      </div>
    </>
  )
}
